if (!customElements.get('bundle-offers')) {
  customElements.define(
    'bundle-offers',
    class BundleOffers extends HTMLElement {
      connectedCallback() {
        this.quantityInput = this.querySelector('input[name="quantity"]');
        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.variantChangeUnsubscriber?.();
      }

      onChange(event) {
        if (event.target.matches('[data-offer-variant]')) {
          event.target.dataset.touched = 'true';
          if (event.target.dataset.item === '1') this.selectMainVariant(event.target);
          return;
        }
        if (!event.target.classList.contains('pp-offer__input')) return;

        this.quantityInput.value = event.target.value;
        this.querySelectorAll('.pp-offer').forEach((offer) => {
          offer.classList.toggle('is-selected', offer.contains(event.target));
        });
        this.querySelectorAll('[data-offer-colors]').forEach((colors) => {
          colors.hidden = colors.dataset.offerColors !== event.target.dataset.offer;
        });
      }

      // Prices depend on the selected variant: copy them from the section HTML fetched by product-info.js
      onVariantChange({ data: { sectionId, html, variant } }) {
        if (sectionId !== this.dataset.section) return;

        // The first item follows the main picker; the others too until the customer picks their colour.
        if (variant) {
          this.querySelectorAll('[data-offer-variant]').forEach((select) => {
            if (select.dataset.item === '1' || select.dataset.touched !== 'true') select.value = String(variant.id);
          });
        }

        const source = html.getElementById(this.id);
        if (!source) return;

        const sourceElements = source.querySelectorAll('[data-offer-dynamic]');
        this.querySelectorAll('[data-offer-dynamic]').forEach((element, index) => {
          if (sourceElements[index]) element.innerHTML = sourceElements[index].innerHTML;
        });
      }

      // Colour of the first item chosen in the pack: select the same value in the main variant picker.
      selectMainVariant(select) {
        const value = select.selectedOptions[0]?.dataset.option;
        const picker = document.getElementById(`variant-selects-${this.dataset.section}`);
        if (!value || !picker) return;

        const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
        const dropdown = picker.querySelector('select');
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (!radio && dropdown && dropdown.value !== value) {
          dropdown.value = value;
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Variants chosen for each item of the selected pack, when they differ from the form's single variant.
      // Returns { mainId, mainQuantity, extraItems } or null when the form can add the pack as is.
      getCartSplit(form) {
        const offer = this.querySelector('.pp-offer__input:checked');
        const colors = offer && this.querySelector(`[data-offer-colors="${offer.dataset.offer}"]`);
        const idInput = form.querySelector('input[name="id"]');
        if (!colors || !idInput) return null;

        const counts = new Map();
        colors.querySelectorAll('[data-offer-variant]').forEach((select) => {
          counts.set(select.value, (counts.get(select.value) || 0) + 1);
        });
        if (counts.size === 1 && counts.has(idInput.value)) return null;

        const [mainId, mainQuantity] = counts.entries().next().value;
        counts.delete(mainId);
        const extraItems = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
        return { mainId, mainQuantity, extraItems };
      }

      // Points the form at the first variant of the split for one submit; returns a function restoring it.
      applyCartSplit(form, { mainId, mainQuantity }) {
        const idInput = form.querySelector('input[name="id"]');
        const previous = [idInput.value, this.quantityInput.value];
        idInput.value = mainId;
        this.quantityInput.value = mainQuantity;
        return () => {
          [idInput.value, this.quantityInput.value] = previous;
        };
      }
    }
  );
}

if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const target = this.querySelector('[data-delivery-dates]');
        const template = target?.dataset.template;
        if (!template || !/\[(min|max)\]/.test(template)) return;

        const minDays = parseInt(this.dataset.minDays, 10) || 0;
        const maxDays = Math.max(parseInt(this.dataset.maxDays, 10) || 0, minDays);
        const locale = document.documentElement.lang || 'fr';
        const format = (date) => date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });

        target.textContent = template
          .replace('[min]', format(this.addBusinessDays(minDays)))
          .replace('[max]', format(this.addBusinessDays(maxDays)));
      }

      addBusinessDays(days) {
        const date = new Date();
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          if (date.getDay() !== 0 && date.getDay() !== 6) added++;
        }
        return date;
      }
    }
  );
}

// "Plus de choix" button of the variant picker. The expanded state is kept on <product-info>,
// because the variant picker itself is re-rendered each time a variant is selected.
if (!window.productExtrasMoreValuesListener) {
  window.productExtrasMoreValuesListener = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-more-values]');
    if (!button) return;
    button.closest('product-info')?.classList.toggle('pp-values-expanded');
  });
}

// Before the main product form submits, add the checked upsells and, for a pack with several colours,
// the other colours. The form then adds the first colour itself, so the cart drawer opens with everything.
if (!window.productExtrasSubmitListener) {
  window.productExtrasSubmitListener = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      // getAttribute: on the product form, `form.id` is shadowed by its <input name="id">.
      const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
      if (!formId || form.dataset.extrasAdded === 'true') return;

      const checkedInputs = Array.from(document.querySelectorAll(`input[data-upsell-for="${formId}"]:checked`));
      const bundle = document.querySelector(`bundle-offers[data-form="${formId}"]`);
      const split = bundle?.getCartSplit(form);
      if (!checkedInputs.length && !split) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const items = checkedInputs
        .map((input) => ({ id: Number(input.value), quantity: 1 }))
        .concat(split ? split.extraItems : []);
      const addExtras = items.length
        ? fetch(`${routes.cart_add_url}.js`, { ...fetchConfig('json'), body: JSON.stringify({ items }) })
        : Promise.resolve();

      addExtras
        .catch((error) => console.error(error))
        .finally(() => {
          checkedInputs.forEach((input) => (input.checked = false));
          const restore = split ? bundle.applyCartSplit(form, split) : null;
          form.dataset.extrasAdded = 'true';
          form.requestSubmit();
          delete form.dataset.extrasAdded;
          restore?.();
        });
    },
    true
  );
}
